import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ExemptedStudent } from "../../../types/common";
import { StandAloneExemptedStudentsContext } from "../../context/EditQuizContext";
import { UseEditQuizStateProps } from "./useEditQuizState";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { hasExemptedStudentMapChanged } from "../../editQuizHelper";
import { ExemptionInfo } from "../../accommodation/exemptionHelper";

type UseStudentStateProps = Pick<
	UseEditQuizStateProps,
	| "quizInfo"
	| "api"
	| "uploadedExemptedStudentsMap"
	| "uploadedProfilesMap"
	| "setUploadedExemptedStudentsMap"
	| "quizType"
>;

/**
 * State and actions related to students, exemptions, standalone
 */
export default function useStudentState({
	api,
	quizInfo,
	uploadedExemptedStudentsMap,
	uploadedProfilesMap,
	setUploadedExemptedStudentsMap,
	quizType,
}: UseStudentStateProps) {
	const { getCourseStudents, getProctoringExemptionStudents, saveProctoringExemptionStudents } = api;
	const initialExemptedStudentsMapRef = useRef<Map<number, ExemptedStudent> | null>(null);
	const uploadedMapRef = useRef(uploadedExemptedStudentsMap); // so fetch exemption info doesnt re render on the accommodation step

	const [exemptionInfo, setExemptionInfo] = useState<ExemptionInfo>({
		nonExemptRoster: new Map(),
		exemptRoster: new Map(),
	});

	const standAloneExemptedStudentsContext = useContext(StandAloneExemptedStudentsContext);

	const numExempted = useMemo(() => exemptionInfo.exemptRoster.size, [exemptionInfo.exemptRoster]);

	useEffect(() => {
		uploadedMapRef.current = uploadedExemptedStudentsMap;
	}, [uploadedExemptedStudentsMap]);

	const setInitialExemptionsRef = useCallback((rosters: { exemptRoster: Map<number, ExemptedStudent> }) => {
		// Only set it once (when it's null)
		if (initialExemptedStudentsMapRef.current === null) {
			initialExemptedStudentsMapRef.current = new Map(rosters.exemptRoster);
		}
	}, []);

	// if creating/ editing a standalone assessment, use the value from standAloneExemptedStudentsContext to set the default value for exemptionInfo

	// When creating a standalone assessment, the uploadedExemptedStudentsMap is set using setUploadedExemptedStudentsMap(exemptionInfo) in ExemptionSection.
	// uploadedExemptedStudentsMap is declared in CreateStandaloneAssessmentSection, and is used to create exemption emails for the standalone assessment.
	const initStandaloneExemptionInfo = useCallback(() => {
		if (quizType !== "standalone") return;
		const nonExemptedStudentsMap = new Map<number, ExemptedStudent>();
		const exemptedStudentsMap = new Map<number, ExemptedStudent>();

		const { studentList, exemptedStudents } = standAloneExemptedStudentsContext;
		const studentListUserIds = new Set(studentList.map((student) => student.userId));

		const exemptedUserIds = new Set(exemptedStudents.map((student) => student.userId));

		const nonExemptedStudents = studentList.filter((student) => !exemptedUserIds.has(student.userId));

		// if this is false, the instructors added/removed students from the standalone student list. Update the exemptionInfo accordingly.
		const isStudentListEqualToStudentsInExemptionInfo =
			studentList.every(
				(student) =>
					exemptionInfo.exemptRoster.has(student.userId) || exemptionInfo.nonExemptRoster.has(student.userId),
			) &&
			Array.from(exemptionInfo.exemptRoster.values())
				.concat(Array.from(exemptionInfo.nonExemptRoster.values()))
				.every((student) => studentListUserIds.has(student.userId));

		if (
			!isStudentListEqualToStudentsInExemptionInfo ||
			(exemptionInfo.exemptRoster.size === 0 && exemptionInfo.nonExemptRoster.size === 0)
		) {
			nonExemptedStudents.forEach((nonExemptedStudent) =>
				nonExemptedStudentsMap.set(nonExemptedStudent.userId, nonExemptedStudent),
			);
			exemptedStudents.forEach((exemptedStudent) => {
				exemptedStudentsMap.set(exemptedStudent.userId, exemptedStudent);
			});

			const exemptionInfo = {
				nonExemptRoster: nonExemptedStudentsMap,
				exemptRoster: exemptedStudentsMap,
			};

			setExemptionInfo(exemptionInfo);
			setInitialExemptionsRef(exemptionInfo);
		}
	}, [
		exemptionInfo.exemptRoster,
		exemptionInfo.nonExemptRoster,
		quizType,
		setInitialExemptionsRef,
		standAloneExemptedStudentsContext,
	]);

	const fetchAndSetExemptionInfo = useCallback(
		async (tempProfileMap: Map<number, ProctoringOptionProfileV2>) => {
			const quizId = quizInfo.quizId;

			if (!quizInfo.courseId) {
				console.warn("Missing courseId; cannot load exemptions.");
				return;
			}

			try {
				const studentRoster = await getCourseStudents(quizInfo);
				let exemptedUserIds: Omit<ExemptedStudent, "name">[] = [];
				if (quizId) {
					exemptedUserIds = await getProctoringExemptionStudents({ ...quizInfo, quizId });
				} else if (uploadedMapRef.current) {
					exemptedUserIds = Array.from(uploadedMapRef.current.values()).map(({ name: _name, ...rest }) => rest);
				}

				const exemptedStudentsMap = new Map(exemptedUserIds.map((e) => [e.userId, e]));

				// Categorize student roster by exemptions using a reduce to build the rosters
				const newExemptionInfo = (studentRoster ?? [])
					.map((s) => ({ userId: s.id, name: s.name }))
					.reduce(
						(prev, s) => {
							const exemptedStudent = exemptedStudentsMap.get(s.userId);

							if (exemptedStudent) {
								const studentProfileId = exemptedStudent.proctoringOptionProfileId;
								prev.exemptRoster.set(s.userId, {
									...s,
									type: exemptedStudent.type,
									accommodationNotes: exemptedStudent.accommodationNotes ?? undefined,
									proctoringOptionProfileId:
										studentProfileId && tempProfileMap.has(studentProfileId) ? studentProfileId : undefined,
								});
							} else {
								prev.nonExemptRoster.set(s.userId, s);
							}
							return prev;
						},
						{
							nonExemptRoster: new Map<number, ExemptedStudent>(),
							exemptRoster: new Map<number, ExemptedStudent>(),
						},
					);

				setExemptionInfo(newExemptionInfo);
				setInitialExemptionsRef(newExemptionInfo);
				return true;
			} catch (err) {
				console.error("Failed to fetch exemption data:", err);
				return false;
			}
		},
		[getCourseStudents, getProctoringExemptionStudents, quizInfo, setInitialExemptionsRef],
	);

	const handleSaveExemptedStudentsNotStandalone = useCallback(
		async (quizId: string) => {
			if (quizType !== "standalone") {
				// only save the exempted students here if we're not creating a standalone assessment
				// (there is a seperate workflow to add exempted students during standalone creation)
				await saveProctoringExemptionStudents({
					assessmentInfo: { courseId: quizInfo?.courseId, quizId },
					students: Array.from(uploadedExemptedStudentsMap.values()).filter(
						(x) => !x.proctoringOptionProfileId || uploadedProfilesMap.has(x.proctoringOptionProfileId),
					),
				});
			}
		},
		[quizInfo?.courseId, saveProctoringExemptionStudents, quizType, uploadedExemptedStudentsMap, uploadedProfilesMap],
	);

	const isExemptedStudentsModified = useMemo(() => {
		return hasExemptedStudentMapChanged(initialExemptedStudentsMapRef.current, uploadedExemptedStudentsMap);
	}, [uploadedExemptedStudentsMap]);

	const updateRoster = useCallback(
		(newRoster: ExemptionInfo) => {
			setExemptionInfo(newRoster);
			if (newRoster.exemptRoster.size >= 0 && newRoster.nonExemptRoster.size >= 0) {
				setUploadedExemptedStudentsMap(newRoster.exemptRoster);
			}
		},
		[setUploadedExemptedStudentsMap],
	);

	return {
		state: {
			exemptionInfo,
			numExempted,
			isExemptedStudentsModified,
		},
		action: {
			initStandaloneExemptionInfo,
			fetchAndSetExemptionInfo,
			updateRoster,
			handleSaveExemptedStudentsNotStandalone,
		},
	};
}
